import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { listStaffTenants } from '../../api/staff';
import { useT, type DictKey } from '../../i18n';
import { formatDateShort } from '../../lib/staffFormat';

const PLAN_KEY: Record<string, DictKey> = {
  free: 'billing.plan.free',
  pro: 'billing.plan.pro',
  business: 'billing.plan.business',
  enterprise: 'billing.plan.enterprise',
};

// ANX-087b — список клиентов с поиском.
export function StaffTenantsPage() {
  const t = useT();
  const [search, setSearch] = useState('');
  const q = useQuery({
    queryKey: ['staff_tenants', search],
    queryFn: () => listStaffTenants(search),
  });
  const tenants = q.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-slate-900">{t('staff.nav.tenants')}</h1>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('staff.tenants.search')}
          className="w-64 rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-accent-500 focus:ring-accent-500"
        />
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2">{t('staff.col.client')}</th>
              <th className="px-4 py-2">{t('staff.col.plan')}</th>
              <th className="px-4 py-2">{t('staff.col.status')}</th>
              <th className="px-4 py-2 text-right">{t('staff.col.seats')}</th>
              <th className="px-4 py-2 text-right">{t('staff.col.dialogsMonth')}</th>
              <th className="px-4 py-2">{t('staff.col.created')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tenants.map((tn) => (
              <tr key={tn.id} className="hover:bg-slate-50">
                <td className="px-4 py-2">
                  <Link
                    to="/staff/tenants/$id"
                    params={{ id: tn.id }}
                    className="font-medium text-accent-700 hover:underline"
                  >
                    {tn.name}
                  </Link>
                  <div className="text-xs text-slate-400">{tn.slug}</div>
                </td>
                <td className="px-4 py-2">{t(PLAN_KEY[tn.plan] ?? 'billing.plan.free')}</td>
                <td className="px-4 py-2">
                  <StatusBadge status={tn.status} />
                </td>
                <td className="px-4 py-2 text-right">{tn.seats_used}</td>
                <td className="px-4 py-2 text-right">{tn.dialogs_month}</td>
                <td className="px-4 py-2 text-slate-500">{formatDateShort(tn.created_at)}</td>
              </tr>
            ))}
            {tenants.length === 0 && !q.isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-slate-400">
                  {t('staff.tenants.empty')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StatusBadge({ status }: { status: string }) {
  const t = useT();
  const cls =
    status === 'active'
      ? 'bg-emerald-50 text-emerald-700'
      : status === 'suspended'
        ? 'bg-amber-50 text-amber-700'
        : 'bg-red-50 text-red-700';
  const key: DictKey =
    status === 'active'
      ? 'staff.status.active'
      : status === 'suspended'
        ? 'staff.status.suspended'
        : 'staff.status.deleted';
  return <span className={'inline-block rounded-full px-2 py-0.5 text-xs font-medium ' + cls}>{t(key)}</span>;
}
